import type { Rep } from "@repo/models";
import type { RepPortalTabs } from "agent-portal";

import { TLPGrid, useGridColumns } from "@repo/baseCloud/components";
import { hasValue, PushErrorNotification } from "@repo/helpers";
import { useSearchParams } from "@repo/helpers";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	Button,
	ButtonTypes,
	MoveableDialog,
	Column,
} from "@repo/ui";
import { useCallback, useEffect, useMemo, useState } from "react";

import type { RepsFilter } from "../../api/repsApi";
import type { RepSettings } from "../../models";

import RepsApi, { controller } from "../../api/repsApi";
import { AgentPortal } from "../../apps/agentPortal";
import { AuditEditor } from "../../apps/auditEditor";
import { getInitialDialogSize } from "../../helpers/agentPortal";
import { BulkImportModal } from "./components/BulkImportModal";
import { useRepsData } from "./hooks";
import style from "./style.module.scss";

interface Props {
	filters: RepsFilter;
	setFilters: (filters: RepsFilter) => void;
	settings?: RepSettings;
}

/** Looked up by the seeded button columns' options.onClick; the GPS button's column is "login". */
const columnCallbacks = {
	openGps: (login: string) => window.open(`/MVC/Map?rep=${encodeURIComponent(login)}`, "_blank"),
};

export function RepsGrid(props: Props) {
	const { filters, setFilters, settings } = props;

	const { repsQueryProxy, refetch } = useRepsData(filters);

	const [dialogOpen, setDialogOpen] = useState(false);
	const [hasAutoOpened, setHasAutoOpened] = useState(false);

	const searchParams = useSearchParams();
	const repIdParam = searchParams.get("representativeId");
	const tabParam = searchParams.get("tab");
	const entityIdParam = searchParams.get("entityId");

	useEffect(() => {
		if (repIdParam && !hasAutoOpened) {
			const id = parseInt(repIdParam);
			const entityId = parseInt(entityIdParam);
			if (!isNaN(id)) {
				if (hasValue(tabParam)) {
					setSelectedTabForPortal(tabParam as RepPortalTabs);
					if (!isNaN(entityId)) {
						setEntityIdForPortal(entityId);
					}
				}
				setRepIdForPortal(id);
				setDialogOpen(true);
				setHasAutoOpened(true);
			}
		}
	}, [repIdParam, hasAutoOpened, tabParam, entityIdParam]);

	const [alertDialogProps, setAlertDialogProps] = useState<{
		open: boolean;
		title: string;
		description: string;
		onConfirm: () => Promise<void>;
	}>({
		open: false,
		title: "",
		description: "",
		onConfirm: async () => Promise.resolve(),
	});
	const [auditDialogOpen, setAuditDialogOpen] = useState(false);
	const [repIdForPortal, setRepIdForPortal] = useState<number | null>(null);
	const [segmentIdForPortal, setSegmentIdForPortal] = useState<number | undefined>(undefined);
	const [selectedTabForPortal, setSelectedTabForPortal] = useState<RepPortalTabs>(undefined);
	const [entityIdForPortal, setEntityIdForPortal] = useState<number | null>(null);
	const [bulkImportOpen, setBulkImportOpen] = useState(false);
	const handleDialogOpenChange = (open: boolean) => {
		setDialogOpen(open);
		if (!open) {
			setRepIdForPortal(null);
			setSegmentIdForPortal(undefined);
			setSelectedTabForPortal(undefined);
		}
	};

	const openPortalModal = (repId: number, tab?: RepPortalTabs, segmentId?: number) => {
		setRepIdForPortal(repId);
		setSegmentIdForPortal(segmentId);
		setSelectedTabForPortal(tab);
		setDialogOpen(true);
	};

	const openPortalModalNew = () => {
		setRepIdForPortal(null);
		setSelectedTabForPortal("PersonalInfo");
		setDialogOpen(true);
	};

	const forceActivate = useCallback(
		async (rep: Rep) => {
			const validationError = await RepsApi.validateActivation(rep.segmentId);
			const text = validationError
				? `Validation Error: ${validationError}. Are you sure?`
				: "Are you sure you want to activate this agent?";

			setAlertDialogProps({
				open: true,
				title: "Activate Agent",
				description: text,
				onConfirm: async () => {
					await RepsApi.forceActivate(rep.segmentId);
					await refetch();
				},
			});
		},
		[refetch],
	);

	const deactivate = useCallback(
		(rep: Rep) => {
			setAlertDialogProps({
				open: true,
				title: "Deactivate Agent",
				description: "Are you sure you want to deactivate this agent?",
				onConfirm: async () => {
					await RepsApi.deactivate(rep.segmentId);
					await refetch();
				},
			});
		},
		[refetch],
	);

	const deactivateAll = useCallback(
		(rep: Rep) => {
			setAlertDialogProps({
				open: true,
				title: "Deactivate Agent",
				description: "Are you sure you want to deactivate this agent?",
				onConfirm: async () => {
					await RepsApi.deactivateAll(rep.repId);
					await refetch();
				},
			});
		},
		[refetch],
	);

	const { columns: dynamicColumns, isLoading: isLoadingColumns } = useGridColumns<Rep>(
		"Representatives",
		RepsApi.getGridColumns,
		columnCallbacks,
	);

	const columns = useMemo(
		() =>
			[
				settings?.userIsAdmin
					? new Column<Rep>("Admin")
							.withFormat((rep: Rep) =>
								!rep.active ? (
									<Button
										color="green"
										title="Activate"
										onClick={() => forceActivate(rep)}
										size="medium"
										className={style.adminButton}
									>
										Activate
									</Button>
								) : (
									<Button
										color="red"
										title="Deactivate"
										onClick={() => (filters.segments ? deactivate(rep) : deactivateAll(rep))}
										size="medium"
										className={style.adminButton}
									>
										Deactivate
									</Button>
								),
							)
							.withSortable(false)
							.withMinWidth(112)
					: null,
				settings?.repPortalEnabled
					? new Column("Portal", "repId")
							.withFullObject()
							.withFormat((rep: Rep) =>
								rep.repId ? (
									<Button
										types={[ButtonTypes.Primary]}
										title="Open in Agent Portal (Summary)"
										onClick={() =>
											openPortalModal(
												rep.repId,
												"AgentSummary",
												filters.segments ? rep.segmentId : undefined,
											)
										}
										size="medium"
										className={style.adminButton}
									>
										Portal
									</Button>
								) : null,
							)
							.withSortable(false)
							.withMinWidth(112)
					: new Column("Edit", "repId")
							.withFullObject()
							.withFormat((rep: Rep) =>
								rep.repId ? (
									<Button
										types={[ButtonTypes.Primary]}
										title="Open in Agent Portal (Edit)"
										onClick={() =>
											openPortalModal(
												rep.repId,
												"PersonalInfo",
												filters.segments ? rep.segmentId : undefined,
											)
										}
										size="medium"
										className={style.adminButton}
									>
										Edit
									</Button>
								) : null,
							)
							.withSortable(false)
							.withMinWidth(112),
				...dynamicColumns,
			].filter(hasValue),
		[settings, forceActivate, filters.segments, deactivate, deactivateAll, dynamicColumns],
	);

	if (isLoadingColumns) return null;

	return (
		<>
			<TLPGrid<Rep, RepsFilter>
				id="repsGrid"
				title="Representatives"
				keyProperty="id"
				filters={filters}
				setFilters={setFilters}
				query={repsQueryProxy}
				refresh={refetch}
				isEnriching={repsQueryProxy.isEnriching}
				columns={columns}
				apiRoute={controller}
				gridApi={RepsApi}
				exportFunc={RepsApi.getReps}
				createButtonText={settings?.canEditReps || settings?.userIsAdmin ? "Create New Agent" : undefined}
				onCreateClick={settings?.canEditReps || settings?.userIsAdmin ? openPortalModalNew : undefined}
				additionalActions={
					settings?.canEditReps || settings?.userIsAdmin ? (
						<Button types={[ButtonTypes.Green]} onClick={() => setBulkImportOpen(true)}>
							Bulk Upload
						</Button>
					) : undefined
				}
				selected={repIdForPortal ? [repIdForPortal] : []}
			/>
			{settings?.auditCodes && (
				<div className="mt-4">
					<Button onClick={() => setAuditDialogOpen(true)}>View Automated Exports</Button>
				</div>
			)}
			<MoveableDialog
				open={dialogOpen}
				onOpenChange={handleDialogOpenChange}
				title="Agent Portal"
				initialSize={getInitialDialogSize()}
				resizable
				movable
				className="agent-portal-modal !p-0"
				contentClassName="!p-0"
			>
				<AgentPortal
					repId={repIdForPortal}
					segmentId={segmentIdForPortal}
					selectedTab={selectedTabForPortal}
					entityId={entityIdForPortal}
					editOnly={!settings?.repPortalEnabled}
				/>
			</MoveableDialog>
			{auditDialogOpen ? (
				<MoveableDialog
					open={auditDialogOpen}
					onOpenChange={setAuditDialogOpen}
					title="Automated Exports"
					initialSize={getInitialDialogSize()}
					resizable
					movable
				>
					<AuditEditor code={settings?.auditCodes} defaultValues={[]} />
				</MoveableDialog>
			) : null}
			<BulkImportModal
				visible={bulkImportOpen}
				onClose={() => setBulkImportOpen(false)}
				onSuccess={() => refetch()}
				showFilters={settings?.showFilters ?? false}
			/>
			<AlertDialog
				open={alertDialogProps.open}
				onOpenChange={(open) => setAlertDialogProps((prev) => ({ ...prev, open }))}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{alertDialogProps.title}</AlertDialogTitle>
						<AlertDialogDescription>{alertDialogProps.description}</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Cancel</AlertDialogCancel>
						<AlertDialogAction
							onClick={async () => {
								try {
									await alertDialogProps.onConfirm();
								} catch (e) {
									// A 403 body can be an IIS error page, so show a fixed message.
									console.error(e);
									PushErrorNotification("Couldn't change the agent's status.");
								}
								setAlertDialogProps((prev) => ({ ...prev, open: false }));
							}}
						>
							Confirm
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
}
